import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle2, ShieldAlert } from 'lucide-react';
import api from '../../services/api';
import FooterMentions from '../../components/common/FooterMentions';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [form, setForm] = useState({ nouveau: '', confirm: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.nouveau.length < 6) {
      return setError('Le mot de passe doit faire au moins 6 caractères');
    }
    if (form.nouveau !== form.confirm) {
      return setError('Les deux mots de passe ne correspondent pas');
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, nouveauMotDePasse: form.nouveau });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Lien invalide ou expiré. Refais une demande.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col p-4" style={{ backgroundColor: '#0f172a' }}>
      <div className="flex-1 flex items-center justify-center w-full">
        <div className="w-full max-w-md">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 shadow-2xl">
            {!token ? (
              <div className="text-center">
                <ShieldAlert size={40} className="mx-auto text-amber-400 mb-3" />
                <h2 className="text-xl font-semibold text-white mb-2">Lien incomplet</h2>
                <p className="text-gray-400 text-sm">
                  Ce lien de réinitialisation est invalide. Refais une demande depuis la page de connexion.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="mt-5 w-full bg-gray-800 hover:bg-gray-700 text-white py-2.5 rounded-lg transition"
                >
                  Retour à la connexion
                </button>
              </div>
            ) : done ? (
              <div className="text-center">
                <CheckCircle2 size={44} className="mx-auto text-green-400 mb-3" />
                <h2 className="text-xl font-semibold text-white mb-2">Mot de passe réinitialisé</h2>
                <p className="text-gray-400 text-sm">
                  Tu vas être redirigé vers la connexion…
                </p>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-white mb-1">Nouveau mot de passe</h2>
                <p className="text-gray-500 text-sm mb-6">Choisis un nouveau mot de passe pour ton compte.</p>
                <form onSubmit={submit} className="space-y-5">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">Nouveau mot de passe</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input
                        type={showPwd ? 'text' : 'password'}
                        value={form.nouveau}
                        onChange={e => setForm({ ...form, nouveau: e.target.value })}
                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-10 pr-10 py-3 focus:outline-none focus:border-yellow-500 transition-colors"
                        placeholder="Min. 6 caractères"
                        required
                        autoComplete="new-password"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd(!showPwd)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                        aria-label={showPwd ? 'Masquer' : 'Afficher'}
                      >
                        {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">Confirmer le mot de passe</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input
                        type={showPwd ? 'text' : 'password'}
                        value={form.confirm}
                        onChange={e => setForm({ ...form, confirm: e.target.value })}
                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:border-yellow-500 transition-colors"
                        placeholder="Re-saisis le mot de passe"
                        required
                        autoComplete="new-password"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-900/30 border border-red-700 text-red-400 rounded-lg px-4 py-3 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black font-bold py-3 rounded-lg transition-all duration-200 disabled:opacity-60"
                  >
                    {loading ? 'Réinitialisation…' : 'Réinitialiser mon mot de passe'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
      <FooterMentions variant="dark" className="relative z-10 mt-4" />
    </div>
  );
}
